import React, {useEffect} from 'react';
import {Button, Col, Container, Row} from 'reactstrap';
import queryString from 'query-string';
import classNames from 'classnames';

import track from 'utils/track';
import useBalanceText from 'utils/useBalanceText';
import useBeacon from 'utils/useBeacon';

import PageMeta from 'components/PageMeta';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import Video from 'components/Video';
import {SalesDisclaimer} from 'components/SalesCta';
import CheckoutButton from 'components/CheckoutButton';
import SpeakersGroupByCollection from 'components/Uhc/UhcSpeaker';
import Footer from 'components/Elements/Footer';
import IconListCL from 'components/ComponentLibrary/IconListCL/IconListCL';
import TestimonialCL from 'components/ComponentLibrary/TestimonialCL';

//@ts-ignore
import data from 'data/uhc.json';
const {uhcTestimonials} = data;

const PRODUCT_SKU = 'UHC21';
const PRODUCT_NAME = 'Ultimate Health Collection';
const PRODUCT_PRICE = 47;
const PRODUCT_CHECKOUT_SLUG = '';

export default function UhcUpsellIndex({product = 'tt2', ...props}) {
  const pageTitle = 'Get the Ultimate Health Collection';
  const pageDescription = 'Make FOOD the foundation of YOUR health';
  const ogImage = 'https://cdn.foodrevolution.org/global/images/placeholder.jpg';
  const shareUrl = 'https://type2.foodrevolution.org/masterclass/';

  const {
    OrderId = '',
    OfferSlug = '',
    UpSellId = ''
  } = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const successUrl = `${process.env.CHECKOUT_URL}/${OfferSlug}/${OrderId}/${UpSellId}/accept`;
  const declineUrl = `${process.env.CHECKOUT_URL}/${OfferSlug}/${OrderId}/${UpSellId}/decline`;

  useEffect(() => {
    track('Product Viewed', {
      product_id: PRODUCT_SKU,
      sku: PRODUCT_SKU,
      name: PRODUCT_NAME,
      value: Number(PRODUCT_PRICE).toFixed(2),
      variant: PRODUCT_CHECKOUT_SLUG,
      currency: 'usd'
    });
  }, []);

  const isHealthFilm = product === 'eft' || product === 'ff' || product === 'ftf' || product === 'tntg';
  const healthFilmPartner = {
    eft: 'Eating Our Way to Extinction LTD',
    ff: 'Louie',
    ftf: 'Plant Pure Communities',
    tntg: 'Earth Conscious Films'
  }[product];
  const healthFilmName = {
    eft: 'Eating For Tomorrow',
    ff: 'Fantastic Fungi',
    ftf: 'From Food to Freedom',
    tntg: 'Need to Grow'
  }[product];
  const healthFilmProductType = {eft: 'Impact Kit', ff: 'Impact Kit', ftf: 'Immersion Kit', tntg: 'Impact Kit'}[
    product
  ];

  const videoUrl = {
    ff: 'https://www.youtube.com/watch?v=2VJRwmU_cVA',
    ftf: 'https://www.youtube.com/watch?v=Vr05iWbCkWQ',
    hhc: 'https://www.youtube.com/watch?v=QEuhg6Wz_cQ',
    ppp: 'https://www.youtube.com/watch?v=yASjLQng890',
    tt2: 'https://www.youtube.com/watch?v=E1q5N5ulWHM',
    wlc: 'https://www.youtube.com/watch?v=aiViTBfH1ec'
  }[product];

  function UhcUpsellCheckoutButton({}) {
    return (
      <CheckoutButton
        className="offer-box-button offer-box-button-upsell"
        sku={PRODUCT_SKU}
        price={Number(PRODUCT_PRICE).toFixed(2)}
        slug={PRODUCT_CHECKOUT_SLUG}
        btnText="Yes, Add to Order"
        badge
        checkoutUrl={process.env.CHECKOUT_URL ?? ''}
        href={successUrl}
      />
    );
  }

  function CtaSection({id, heading = undefined, trees = false, ...sectionProps}) {
    return (
      <SectionCL id={id} color="purple" {...sectionProps} type="triangle">
        <Container>
          <Row>
            <Col>
              <div className={classNames(`background-white rounded text-center p-4 p-lg-5`)}>
                {heading && <div>{heading}</div>}
                <img
                  src="https://cdn.foodrevolution.org/uhc/uhc21-product-image.png"
                  width="1000"
                  height="416"
                  alt="Ultimate Health Collection product image"
                  style={{maxHeight: '300px', width: 'auto'}}
                />
                <UhcUpsellCheckoutButton />
                <p className="balance-text mt-3">
                  Clicking “Yes” above will add the <b>Ultimate Health Collection</b> to your purchase now for an
                  additional $47.
                </p>
                <Button className="btn-upsell-gray mt-4 mb-3" href={declineUrl}>
                  No, thanks.
                </Button>
                <p className="balance-text">
                  Clicking the “No” button above will take you to the products already in your cart.
                </p>
                <SalesDisclaimer />
              </div>
              {trees && (
                <>
                  <Row className="trees-for-future text-white d-flex align-items-center justify-content-center mt-5">
                    <Col xs="11" lg="8" className="mb-4 mb-lg-0">
                      <h6 className="section-only-heading text-center text-lg-left text-400">
                        For every new order, we make a donation to Trees for the Future, supporting their planting of
                        organic fruit or nut trees in low-income communities.
                      </h6>
                    </Col>
                    <Col xs="11" lg="3" className="text-center">
                      <img
                        className={classNames(`logo-trees`)}
                        alt="Trees for the Future logo"
                        src="https://cdn.foodrevolution.org/global/trees-logo-white.png"
                        width={1080}
                        height={442}
                        style={{maxHeight: '100px', width: 'auto'}}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </SectionCL>
    );
  }

  useBeacon();
  useBalanceText();
  return (
    <div className="page page-nosocial">
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={ogImage} />

      <div className="page-content">
        <SectionCL
          id="section-uhc-header"
          order="first"
          innerClass="text-white text-center"
          color="black"
          type="triangle"
        >
          <Container>
            <Row className="mb-4">
              <Col>
                {((product) => {
                  switch (product) {
                    case 'ff':
                      return (
                        <>
                          <h3 className="section-heading mb-2">
                            A Special, One-Time Offer For Our <i>Fantastic Fungi Impact Kit</i> Community
                          </h3>
                          <h5 className="m-0">(You won’t see this special offer again!)</h5>
                        </>
                      );
                    case 'ftf':
                      return (
                        <>
                          <h3 className="section-heading mb-2">
                            A Special, One-Time Offer For Our <i>From Food to Freedom</i> Immersion Kit Community
                          </h3>
                          <h5 className="text-400 m-0">
                            <i>
                              One quick thing before you go. <br className="d-none d-sm-block d-lg-none" />
                              You’ll never see this opportunity again.
                            </i>
                          </h5>
                        </>
                      );
                    case 'hhc':
                      return (
                        <>
                          <h3 className="section-heading mb-2">
                            Thank you for joining <i>Healthy Heart!</i>
                          </h3>
                          <h5 className="text-400 m-0">
                            <i>
                              One quick thing before you go. <br className="d-none d-sm-block d-lg-none" />
                              You’ll never see this opportunity again.
                            </i>
                          </h5>
                        </>
                      );
                    case 'ppp':
                      return (
                        <>
                          <h3 className="section-heading mb-2">
                            Before you dive into the <i>Plant-Powered Playbook,</i> check out this video.
                          </h3>
                          <h5 className="m-0">(You won’t see this special offer again!)</h5>
                        </>
                      );
                    case 'tt2':
                      return (
                        <>
                          <h3 className="section-heading mb-2">
                            Before you dive into <i>Tackling Type 2,</i> check out this video and your special one-time
                            offer.
                          </h3>
                          <h5 className="text-400 m-0">
                            <i>
                              One quick thing before you go. <br className="d-none d-sm-block d-lg-none" />
                              You’ll never see this opportunity again.
                            </i>
                          </h5>
                        </>
                      );
                    case 'wlc':
                      return (
                        <>
                          <h3 className="section-heading mb-2">
                            Before you dive into WHOLE Life Club, check out this video.
                          </h3>
                          <h5 className="m-0">(You won’t see this special offer again!)</h5>
                        </>
                      );
                    default:
                      return null;
                  }
                })(product)}
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col lg="10" className="video-col px-0 px-sm-3">
                <Video url={videoUrl} label={`${product} - UHC Upsell`} playing />
              </Col>
            </Row>

            {isHealthFilm && (
              <Row>
                <Col>
                  <h4 className="text-center mt-4">
                    If one of your goals in getting the {healthFilmProductType} is improving your own health, this is
                    for you.
                  </h4>
                </Col>
              </Row>
            )}
          </Container>
        </SectionCL>

        {isHealthFilm && (
          <SectionCL id="section-uhc-frn" color="white" innerClass="text-center" type="triangle">
            <Container>
              <Row className="justify-content-center">
                <Col lg="10">
                  <img
                    className="mb-4"
                    src="https://cdn.foodrevolution.org/global/frn-logo-2024-trademarked-color.svg"
                    style={{maxHeight: '100px'}}
                  />
                  <h4 className="section-heading">
                    <i>Who is Food Revolution Network?</i>
                  </h4>
                  <p>
                    For over a decade, we’ve been at the forefront of the healthy living movement. We and our 700,000+
                    members are committed to healthy, ethical, and sustainable food for all.
                  </p>
                  <p>
                    We’ve partnered with {healthFilmPartner}
                    and the <i>{healthFilmName}</i> team to bring this film into the world and to create and share the
                    {healthFilmProductType}.
                  </p>
                  {product === 'ftf' && (
                    <p>
                      We want to make sure you know how to eat to prevent and reverse disease — and transform how you
                      look and feel each day.
                    </p>
                  )}
                </Col>
              </Row>
            </Container>
          </SectionCL>
        )}

        <SectionCL
          id="section-uhc-lifespan-2"
          imgPosition="left"
          imgUrl="https://cdn.foodrevolution.org/uhc/images/1288965935-square.jpg"
          color="light-gray"
          type="triangle"
        >
          <h3 className="section-heading">What do people who live to be 100 (and healthy!) have in common?</h3>
          <h5 className="mt-0">
            The people with the <i>longest lifespans</i> know how to live and what to eat to:
          </h5>
          <IconListCL>
            <>
              Live an exceptionally healthy life without the common diseases that afflict masses of people in other
              areas of the world.
            </>
            <>
              Avoid the stress of crushing medical debt, insurance headaches, and a calendar full of nonstop doctor’s
              appointments.
            </>
            <>Say goodbye to sickness and hello to health — without an armload of expensive supplements.</>
            <>Stay at an ideal weight for life without even thinking about it.</>
            <>
              Enjoy energy all day without depending on caffeine, sugar, or other stimulants. (And fall asleep easily at
              night!)
            </>
          </IconListCL>
        </SectionCL>

        <SectionCL
          id="section-uhc-info"
          className="section-uhc-info"
          innerClass="text-center"
          color="white"
          type="triangle"
        >
          <Container>
            <Row>
              <Col>
                <h5 className="section-heading">
                  John Robbins, Food Revolution Network’s co-founder and two-million-copy bestselling author, has
                  interviewed more than 100 of the top food experts on the planet to deliver their cutting-edge wisdom
                  to millions via the world-famous Food Revolution Summit.
                </h5>
                <p className="balance-text">
                  <b>
                    Now’s your chance to get 20 of our best and most popular health-boosting interviews in the Ultimate
                    Health Collection.
                  </b>
                </p>
              </Col>
            </Row>
            <Row className="justify-content-center">
              <Col md="10" lg="8" xl="6">
                <img src="https://cdn.foodrevolution.org/uhc/frn-uhc-logo.svg" />
              </Col>
            </Row>
            <Row className="justify-content-center">
              <Col>
                <h4 className="text-400 mt-3">Add the Ultimate Health Collection to your cart now for $47</h4>
                {{ff: null, hhc: <p>Get the best health of your life.</p>}[product]}
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <CtaSection
          id="section-uhc-offer-1"
          heading={<h3 className="section-heading">One-Time Offer — Save 97%!</h3>}
          trees
        />

        <SectionCL id="section-uhc-about" color="light-gray" type="triangle">
          <Container>
            <Row className="justify-content-center">
              <Col>
                <h2 className="section-heading text-center">About the Ultimate Health Collection</h2>
                <h4 className="text-center mt-0">
                  Ready to get the top insights for lasting health? Make it happen with the help of John Robbins and his
                  unmatched interviews with the world’s top natural health experts.
                </h4>

                <div className="background-white rounded box-shadow-black-25 p-4 p-lg-5 my-5">
                  <Row className="align-items-center justify-content-center">
                    <Col lg="4" className="text-center order-lg-2 mb-4 mb-lg-0">
                      <img
                        className="rounded-circle"
                        src="https://cdn.foodrevolution.org/global/team/john-headshot-crop-20230329.jpg"
                        style={{maxHeight: '250px'}}
                      />
                    </Col>
                    <Col lg="8" className="order-lg-1">
                      <p>Hi, I’m John Robbins.</p>
                      <p>
                        For over 30 years, I’ve been helping people get the best health of their lives. Between writing
                        my multi-million-copy bestselling books like <i>Diet For a New America,</i> and co-founding Food
                        Revolution Network, exposing the truth about the industrial food complex, and conducting
                        hundreds of interviews with world-renowned health revolutionaries, I know what it takes to
                        create lasting health.
                      </p>
                    </Col>
                  </Row>
                  <p>
                    The Ultimate Health Collection includes 20 interviews, each with downloadable MP3s and transcripts.
                    Soak up wisdom in this curated collection from some of <b>the world’s top health experts,</b>{' '}
                    including multiple <i>New York Times</i> bestselling authors like Tony Robbins and Michael Pollan.
                  </p>
                  <p>
                    <b>Do you need the Ultimate Health Collection to get and stay healthy? </b>
                  </p>
                  <p>
                    No, only if you want to do so in the quickest, least expensive, and most enjoyable way possible —
                    while avoiding the mistakes and pitfalls that happen when you try to figure it out by yourself.
                  </p>
                  <p>
                    <b>Most people can get the basics down: drink water, don’t smoke, don’t eat fast food.</b>
                  </p>
                  <p>
                    <b>
                      But how do you get <i>extraordinary</i> health?
                    </b>{' '}
                    What does it take to avoid disease, increase energy, live longer, improve immunity, and stay at a
                    healthy weight? What are the secrets of people who live in the world’s Blue Zones with the longest
                    lifespans?
                  </p>
                  <p>
                    That’s why for over a decade, I’ve interviewed the{' '}
                    <b>top visionaries leading the charge toward a healthier future for all of us.</b> We’ve curated the
                    special Ultimate Health Collection to bring you the very best of those interviews.
                  </p>
                  <p>
                    The only other way to buy this beloved collection would be to purchase all of our past Food
                    Revolution Summits and spend more than $1,500 in the process. But right now (and now only), you can
                    get the Ultimate Health Collection for a mere fraction of that cost — just $47!
                  </p>
                </div>
                <div className="text-center">
                  <UhcUpsellCheckoutButton />
                  <SalesDisclaimer />
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="section-uhc-testimonials" color="white" type="triangle">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center mb-5">
                  What People Are Saying About Food Revolution Network Interviews
                </h2>
                {['uhcRajivB', 'uhcGinnyO', 'uhcAnnS'].map((key) => (
                  <TestimonialCL {...uhcTestimonials[key]} key={key} />
                ))}
                <p className="text-center text-16 mt-5 mb-0">
                  <i>These testimonials reflect people’s unique experiences. Your specific experience will vary.</i>
                </p>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL
          id="section-uhc-imagine-1"
          imgPosition="right"
          imgUrl="https://cdn.foodrevolution.org/uhc/images/920522262-square.jpg"
          color="light-gray"
          type="triangle"
          angleBottomDisplay="lg-none"
        >
          <Container>
            <Row>
              <Col>
                <h5 className="section-heading">Imagine being able to:</h5>
                <IconListCL>
                  <>Live longer</>
                  <>Live better</>
                  <>Prevent cancer</>
                  <>Achieve peak performance</>
                  <>Increase immunity</>
                  <>Prevent Alzheimer’s</>
                  <>Reverse symptoms & early warning signs</>
                  <>Master the psychology of eating</>
                  <>Use food to improve your immunity</>
                </IconListCL>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL
          id="section-uhc-imagine-2"
          imgPosition="left"
          imgUrl="https://cdn.foodrevolution.org/uhc/images/1288965935-square.jpg"
          color="light-gray"
          type="triangle"
          innerClass="sectionCL-padding-lg"
        >
          <Container>
            <Row>
              <Col>
                <IconListCL className="mt-0">
                  <>Shed excess pounds</>
                  <>Feel better than you have in years</>
                  <>Sleep deeply</>
                  <>Nourish yourself</>
                  <>End inflammation</>
                  <>Quit sugar</>
                  <>Love your body exactly as it is</>
                  <>Enjoy incredible digestion</>
                  <>Share what you learn with your loved ones</>
                </IconListCL>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="section-uhc-speakers" color="white" type="triangle">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center mb-5">Learn From These Top Health Experts</h2>
              </Col>
            </Row>
            <Row>
              <SpeakersGroupByCollection />
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="section-uhc-testimonials-2" color="light-gray" type="triangle">
          <Container>
            <Row>
              <Col>
                {['uhcMikeG', 'uhcMyraA', 'uhcMollyS'].map((key) => (
                  <TestimonialCL {...uhcTestimonials[key]} key={key} style="light" />
                ))}
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <CtaSection
          id="section-uhc-offer-2"
          heading={<h3 className="section-heading">One-Time Offer — Save 97%!</h3>}
          order="last"
          angle="none"
          trees
        />
      </div>

      <Footer />
    </div>
  );
}
